import { ActionIcon, Alert, Box, Button, Divider, Group, Stack, Text, TextInput, Title } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { Totals } from '../../models/Checkout/Totals'

interface CartSummaryProps {
  totals: Totals
  onSubmit: () => void
  isButtonDisabled: boolean
  buttonText: string
  isLoadingSubmit?: boolean
  buttonDisabledText: string
  hideButton?: boolean
  discount?: number
  bundleTotal?: number
  couponCode?: string
  appliedCoupon?: string | null
  couponError?: string | null
  couponSuccess?: string | null
  isApplyingCoupon?: boolean
  onCouponCodeChange?: (code: string) => void
  onApplyCoupon?: () => void
  onRemoveCoupon?: () => void
  /** True when a free coupon covers items that had auto-renew enabled */
  autoRenewWillBeDisabled?: boolean
}

function CartSummary(props: CartSummaryProps) {
  const {
    totals,
    isButtonDisabled,
    onSubmit,
    buttonText,
    isLoadingSubmit,
    buttonDisabledText,
    hideButton = false,
    discount,
    bundleTotal = 0,
    couponCode,
    appliedCoupon,
    couponError,
    couponSuccess,
    isApplyingCoupon,
    onCouponCodeChange,
    onApplyCoupon,
    onRemoveCoupon,
    autoRenewWillBeDisabled,
  } = props

  const showCoupon = onCouponCodeChange !== undefined
  const isMobile = useMediaQuery('(max-width: 768px)')
  const effectiveDueNow = totals.totalDueNow + bundleTotal
  const effectiveTotal = totals.totalAmount + bundleTotal
  const originalTotal = effectiveTotal + (discount ?? 0)
  const isFreeWithCoupon = appliedCoupon && effectiveTotal === 0

  return (
    <Box
      p={24}
      style={{
        backgroundColor: 'var(--mantine-color-gray-0)',
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: '20px',
        overflow: 'hidden',
      }}
    >
      <Stack gap={20}>
        <Title size='h3'>Order Summary</Title>

        {isFreeWithCoupon ? (
          /* ── Celebration view when fully discounted ── */
          <Stack gap={12} align='center' py={8}>
            <Text
              size='xl'
              c='dimmed'
              td='line-through'
              fw={500}
            >
              ${originalTotal.toFixed(2)}
            </Text>
            <Title
              size='3rem'
              fw={800}
              style={{ color: 'var(--mantine-color-green-7)', lineHeight: 1 }}
            >
              FREE
            </Title>
            <Group gap={6}>
              <Box
                px={12}
                py={5}
                style={{
                  backgroundColor: 'var(--mantine-color-green-0)',
                  border: '1px solid var(--mantine-color-green-3)',
                  borderRadius: 999,
                }}
              >
                <Text size='sm' c='green' fw={600}>
                  🎉 {appliedCoupon}
                </Text>
              </Box>
              <ActionIcon
                variant='subtle'
                color='gray'
                size='sm'
                radius='xl'
                onClick={onRemoveCoupon}
                aria-label='Remove coupon'
              >
                ✕
              </ActionIcon>
            </Group>
          </Stack>
        ) : (
          /* ── Standard view ── */
          <Stack gap={16}>
            <Group justify='space-between'>
              <Text size='sm' c='dimmed'>Due Now</Text>
              <Text size='sm' fw={500}>
                {discount ? (
                  <Text component='span' c='dimmed' td='line-through' mr={6}>
                    ${(effectiveDueNow + discount).toFixed(2)}
                  </Text>
                ) : null}
                ${effectiveDueNow.toFixed(2)}
              </Text>
            </Group>
            {totals.totalDueAfterApproval > 0 && (
              <Group justify='space-between'>
                <Text size='sm' c='dimmed'>Due After Approval</Text>
                <Text size='sm' fw={500}>
                  ${totals.totalDueAfterApproval.toFixed(2)}
                </Text>
              </Group>
            )}
            {bundleTotal > 0 && (
              <Group justify='space-between'>
                <Text size='sm' c='dimmed'>All Locations Bundle(s)</Text>
                <Text size='sm' fw={500}>${bundleTotal.toFixed(2)}</Text>
              </Group>
            )}
            {discount !== undefined && discount > 0 && (
              <Group justify='space-between'>
                <Text size='sm' c='green'>Discount</Text>
                <Text size='sm' fw={500} c='green'>-${discount.toFixed(2)}</Text>
              </Group>
            )}
            <Divider />
            <Group justify='space-between'>
              <Title size={isMobile ? 'h4' : 'h3'}>Total</Title>
              <Title size={isMobile ? 'h4' : 'h3'} ta='right'>
                ${effectiveTotal.toFixed(2)}
              </Title>
            </Group>

            {showCoupon && (
              <>
                <Divider />
                {!appliedCoupon ? (
                  <Group gap={8} align='flex-end'>
                    <TextInput
                      label='Coupon Code'
                      placeholder='Enter coupon code'
                      value={(couponCode ?? '').toUpperCase()}
                      onChange={(e) => onCouponCodeChange!(e.currentTarget.value.toUpperCase())}
                      onKeyDown={(e) => { if (e.key === 'Enter' && couponCode?.trim()) onApplyCoupon?.() }}
                      style={{ flex: 1 }}
                      error={couponError}
                      size='sm'
                    />
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={onApplyCoupon}
                      loading={isApplyingCoupon}
                      disabled={!couponCode?.trim()}
                      mb={couponError ? 22 : 0}
                    >
                      Apply
                    </Button>
                  </Group>
                ) : (
                  <Group gap={6} align='center'>
                    <Text size='sm' c='green' fw={500} style={{ flex: 1 }}>
                      {couponSuccess}
                    </Text>
                    <Button variant='subtle' size='xs' color='red' onClick={onRemoveCoupon}>
                      Remove
                    </Button>
                  </Group>
                )}
              </>
            )}
          </Stack>
        )}

        {isFreeWithCoupon && autoRenewWillBeDisabled && (
          <Alert color='yellow' radius='md' py={10}>
            <Text size='xs'>
              <strong>Auto-renew will be disabled</strong> — free orders cannot be renewed automatically. You'll need to check out again when your campaign ends.
            </Text>
          </Alert>
        )}

        {!hideButton && (
          <Button
            fullWidth
            title={isButtonDisabled ? buttonDisabledText : ''}
            size='md'
            radius={12}
            onClick={onSubmit}
            disabled={isButtonDisabled}
            loading={isLoadingSubmit}
          >
            {buttonText}
          </Button>
        )}
      </Stack>
    </Box>
  )
}

export default CartSummary
